import React from 'react';

import { Link, useNavigate } from 'react-router-dom';

import {
	Box,
	Button,
	createStyles,
	Divider,
	Flex,
	Input,
	Stack,
	TFHTMLText,
	TFNotifier,
	TFText,
	Tooltip,
	UnstyledButton,
} from '@tf/ui';
import { D, fmt, getObjectDisplayName, isValidEmail, splitObjectId } from '@tf/utils';

import { useCreatePaymentCaseMutation, usePaymentCases } from '@/core/api/payments';
import type { CaseWithRequestMetadata } from '@/core/types/cases';
import { useNestedPaymentForms } from '@/components/payments/useNestedPaymentForms';
import { CaseStatusBadge } from '@/components/shared';

const useStyles = createStyles(({ colors }) => ({
	container: {
		fontSize: 13,
		lineHeight: 1.6,
		fontWeight: 400,
		color: colors.gray[9],

		'&, & *': {
			margin: 0,
			padding: 0,
			border: 0,
			listStyle: 'none',
			textDecoration: 'none !important',
			color: 'inherit !important',
			fontFamily: 'inherit !important',
			fontSize: 'inherit !important',
			lineHeight: 'inherit !important',
			fontWeight: 'inherit !important',
			pointerEvents: 'none',
		},
	},
	caseId: {
		fontSize: 14,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

interface Props {
	paymentId: string;
	decisionWasMade: boolean;
}

export const PaymentCasesList: React.FC<Props> = ({ paymentId, decisionWasMade }) => {
	const [email, setEmail] = React.useState({ value: '', error: '' });

	const navigate = useNavigate();

	const createPaymentCaseMutation = useCreatePaymentCaseMutation();
	const { objectName } = splitObjectId(paymentId);
	const { profile } = useNestedPaymentForms(objectName);
	const { data: cases } = usePaymentCases(paymentId);

	return (
		<Box p="16px">
			{!decisionWasMade && (
				<Flex align="flex-end" gap="16px">
					<Input.Wrapper flex={1} required label="Enter email for case creation">
						<Input
							placeholder="Enter email"
							error={email.error}
							value={email.value}
							onChange={(e) => setEmail({ error: '', value: e.target.value })}
						/>
					</Input.Wrapper>
					<Button
						disabled={createPaymentCaseMutation.isPending}
						onClick={async () => {
							if (!profile) return;

							if (!isValidEmail(email.value)) {
								setEmail((prevState) => ({ ...prevState, error: 'Invalid email' }));
								return;
							}

							createPaymentCaseMutation.mutateAsync(
								{
									paymentId,
									profileId: profile?.id,
									recipientEmail: email.value,
								},
								{
									onSuccess: (res) => {
										TFNotifier.success('Case created successfully');

										const { objectName } = splitObjectId(res[0].id);
										navigate(`cases/${objectName}`);
									},
								}
							);
						}}
					>
						Create case
					</Button>
				</Flex>
			)}
			{Boolean(cases?.length) && (
				<>
					{!decisionWasMade && <Divider m="12px 0" size="1px" color="#E5E7EB" />}
					<Box>
						{cases?.map((c, index) => (
							<CaseListItem key={c.case.id} caseObject={c} hasDivider={index !== cases?.length - 1} />
						))}
					</Box>
				</>
			)}
		</Box>
	);
};

const CaseListItem = ({
	caseObject,
	hasDivider,
}: {
	caseObject: CaseWithRequestMetadata;
	hasDivider: boolean;
}) => {
	const { classes } = useStyles();
	const {
		case: {
			id,
			aliases,
			labels: { status: caseStatus },
			meta: { ['@createdAt']: createdAt },
		},
		request: {
			data: { request_text: htmlText } = {},
			labels: { status: requestStatus },
		},
	} = caseObject;
	const { objectName } = splitObjectId(id);

	const displayName = getObjectDisplayName(aliases);
	const route = `cases/${objectName}`;
	const elapsed = D.calculateTillNow(createdAt);
	const tooltipLabel = fmt.toDate(createdAt, { preset: 'full_date_with_time' });

	return (
		<Stack gap="0">
			<Flex justify="space-between" align="center">
				<Box w="100%">
					<Flex justify="space-between" align="top" mb="4px">
						<Flex align="center">
							<UnstyledButton component={Link} to={route} className={classes.caseId}>
								<TFText mr="8px" fw="500" fz="14px">
									Case #{displayName}
								</TFText>
							</UnstyledButton>
							<CaseStatusBadge caseStatus={caseStatus} caseRequestStatus={requestStatus} />
						</Flex>
						<Tooltip label={tooltipLabel} position="top">
							<TFText fw="400" fz="13px" c="#6B7280">
								{elapsed}
							</TFText>
						</Tooltip>
					</Flex>
					<TFText c="#6b7280" lineClamp={1}>
						{htmlText ? (
							<TFHTMLText text={htmlText} className={classes.container} />
						) : (
							'No case description provided'
						)}
					</TFText>
				</Box>
			</Flex>
			{hasDivider && <Divider m="12px 0" size="1px" color="#E5E7EB" />}
		</Stack>
	);
};
